import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import theme from "../../theme/theme";

interface ExtendedXAxisTitleOptions extends Highcharts.XAxisTitleOptions {
  enabled: boolean;
}
interface ExtendedChart extends Highcharts.Chart {
  crosshair: Highcharts.SVGElement;
}
interface ExtendedSeriesLineOptions extends Highcharts.SeriesLineOptions {
  enabledCrosshairs: boolean;
}

const INF_Chart: React.FC<any> = (props) => {
  const {
    type,
    splineWidth,
    width,
    height,
    dataPoints,
    format,
    graphSequence,
    tooltipShow,
    isCrosshair,
    dataLabels,
    tabIdentity,
    pageName,
    xAxisArray,
    xAxisInterval,
    graphTitle,
    selectedAnalyticListItem,
  } = props;
  const [appTheme, setAppTheme] = useState(theme?.defaultTheme);

  let collectedArrayValues: number[] = [];
  let minValue: number = 0;
  let maxValue: number = 0;

  dataPoints &&
    dataPoints[0].data.map((data: any) => {
      collectedArrayValues.push(data[0]);
    });

  minValue = Math.min(...collectedArrayValues);
  maxValue = Math.max(...collectedArrayValues);

  const [selectedTheme, setSelectedTheme] = useState(
    JSON.parse(localStorage.getItem("theme")!)
  );

  useEffect(() => {
    switch (selectedTheme) {
      case "red":
        setAppTheme(theme?.redTheme);
        break;
      case "green":
        setAppTheme(theme?.greenTheme);
        break;
      case "yellow":
        setAppTheme(theme?.yellowTheme);
        break;
      case "default":
        setAppTheme(theme?.defaultTheme);
        break;
      default:
        setAppTheme(theme?.defaultTheme);
        break;
    }
  }, [selectedTheme]);

  const [toolTipBg, setToolTipBg] = useState<string>();
  const [tBorder, setTBorder] = useState<string>();

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={{
        chart: {
          type: type,
          plotBackgroundColor: "transparent",
          backgroundColor: "transparent",
          height: height,
          width: width,
        },
        scrollbar: {
          barBackgroundColor: "gray",
          barBorderRadius: 7,
          barBorderWidth: 0,
          buttonBackgroundColor: "gray",
          buttonBorderWidth: 0,
          buttonBorderRadius: 7,
          trackBackgroundColor: "none",
          trackBorderWidth: 1,
          trackBorderRadius: 8,
          trackBorderColor: appTheme?.palette?.chartElement?.grayShade,
        },
        title: false,
        legend: false,

        xAxis: {
          lineColor: appTheme?.palette?.chartElement?.lightGrayShade,
          min: type === "spline" || type === "areaspline" ? 0.49 : 0,
          categories: xAxisArray,
          tickWidth: 0,
          tickLength: 0,
          tickInterval: xAxisInterval,
          showFirstLabel: true,
          showLastLabel: true,
          startOnTick: false,
          endOnTick: false,
          gridLineDashStyle: "LongDash",
          useHTML: true,
          lineWidth: pageName === "infoDialogue" ? 1 : 0,
          crosshair:
            isCrosshair && (type === "spline" || type === "areaspline")
              ? {
                  enabled: true,
                  width: 1,
                  color: "gray",
                  dashStyle: "ShortDash",
                  snap: true,
                }
              : false,
          type: "datetime",
          labels: {
            useHTML: true,
            overflow: "justify",
            format: format,
          },

          tickPositioner: function (this: any) {
            const ticks: any = this.tickPositions;
            if (!ticks.includes(this.dataMax)) ticks.push(this.dataMax);
            ticks.sort((a: any, b: any) => a - b);
            while (ticks[ticks.length - 1] > this.dataMax) {
              ticks.pop();
            }
            return ticks;
          },
        },
        yAxis: {
          title: false,
          visible: true,
          min:
            selectedAnalyticListItem === "Overall Analytics"
              ? graphTitle === "Equipment Efficiency (%)"
                ? 50
                : 20
              : 10,
          max: maxValue,
          tickInterval:
            selectedAnalyticListItem === "Overall Analytics"
              ? graphTitle === "Equipment Efficiency (%)"
                ? 25
                : 20
              : 20,
          startOnTick: false,
          lineColor: appTheme?.palette?.chartElement?.lightGrayShade,
          lineWidth: 1,
          gridLineWidth: pageName === "infoDialogue" ? 0 : 1,
          minorGridLineWidth: 1,
          gridLineDashStyle: "LongDash",
          plotLines:
            pageName !== "infoDialogue"
              ? [
                  {
                    value: maxValue,
                    zIndex: 2,
                    width: 0.8,
                    dashStyle: "Dash",
                    color: "#F00C0C",
                  },
                  {
                    value: minValue,
                    zIndex: 2,
                    width: 0.8,
                    dashStyle: "Dash",
                    color: "#F00C0C",
                  },
                ]
              : [
                  {
                    value: 0,
                    zIndex: 2,
                    width: 0.8,
                    dashStyle: "Dash",
                    color: "#C4C4C4",
                  },
                ],
        },

        tooltip: {
          shared: false,
          useHTML: true,
          backgroundColor: toolTipBg,
          borderColor: tBorder,
          padding: 4,
          className: "tooltipStyle",
          style: {
            color: appTheme?.palette?.chartElement?.colorWhite,
            fontWeight: "bold",
          },
          formatter: function (
            this: Highcharts.TooltipFormatterContextObject
          ): string | boolean {
            if (tooltipShow) {
              const value: any = this.y;

              switch (graphSequence) {
                case "graph1":
                  setToolTipBg(
                    pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? "#9486DA"
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#0C8ACC"
                      : graphTitle === "Operating Hours"
                      ? "#7979D7"
                      : appTheme?.palette?.chartElement?.maroonShades
                  );
                  setTBorder(
                    pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? "#9486DA"
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#0C8ACC"
                      : graphTitle === "Operating Hours"
                      ? "#7979D7"
                      : appTheme?.palette?.chartElement?.maroonShades
                  );
                  break;
                case "graph2":
                  setToolTipBg(
                    pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? "#9486DA"
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#ED6A6A"
                      : appTheme?.palette?.chartElement?.navyBlue
                  );
                  setTBorder(
                    pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? "#9486DA"
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#ED6A6A"
                      : appTheme?.palette?.chartElement?.navyBlue
                  );
                  break;
                case "graph3":
                  setToolTipBg(
                    pageName === "analyticsPage"
                      ? graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#9486DA"
                      : "#7979D7"
                  );
                  setTBorder(
                    pageName === "analyticsPage"
                      ? graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#9486DA"
                      : "#7979D7"
                  );
                  break;
                case "graph4":
                  setToolTipBg(
                    pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? "#9486DA"
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#949FFF"
                      : "#65D9F2"
                  );
                  setTBorder(
                    pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? "#9486DA"
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? "#949FFF"
                        : "#949FFF"
                      : "#65D9F2"
                  );
                  break;
                case "graph5":
                  setToolTipBg(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#949FFF"
                  );
                  setTBorder(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#949FFF"
                  );
                  break;
                case "graph6":
                  setToolTipBg(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#FF718B"
                  );
                  setTBorder(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#FF718B"
                  );
                  break;
                case "graph7":
                  setToolTipBg(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#FABB70"
                  );
                  setTBorder(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#FABB70"
                  );
                  break;
                case "graph8":
                  setToolTipBg(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#00A3FF"
                  );
                  setTBorder(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#00A3FF"
                  );
                  break;
                case "graph9":
                  setToolTipBg(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#D365D6"
                  );
                  setTBorder(
                    graphTitle === "Energy Consumed (kWh)"
                      ? "#9486DA"
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? "#949FFF"
                      : "#D365D6"
                  );
                  break;
              }

              return ` <table>
          <tr>
            <td style="text-align: center;">
                ${
                  graphSequence === "graph1"
                    ? pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? `${value}kWh`
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Predictive Maintenance Hours (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Equipment Efficiency (%)"
                        ? `${value}%`
                        : graphTitle === "Raw Material Level (T)"
                        ? `${value}T`
                        : graphTitle === "Temperature (°C)"
                        ? `${value}°C`
                        : graphTitle === "Humidity (%)"
                        ? `${value}%`
                        : `${value}`
                      : graphTitle === "Operating Hours" ||
                        graphTitle === "Hours of Operation"
                      ? `${value}Hrs`
                      : `${value}°C`
                    : graphSequence === "graph2"
                    ? pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? `${value}kWh`
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Predictive Maintenance Hours (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Equipment Efficiency (%)"
                        ? `${value}%`
                        : graphTitle === "Raw Material Level (T)"
                        ? `${value}T`
                        : graphTitle === "Temperature (°C)"
                        ? `${value}°C`
                        : graphTitle === "Humidity (%)"
                        ? `${value}%`
                        : `${value}`
                      : graphTitle === "Operating Hours" ||
                        graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : `${value}%`
                    : graphSequence === "graph3"
                    ? pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? `${value}kWh`
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Predictive Maintenance Hours (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Equipment Efficiency (%)"
                        ? `${value}%`
                        : graphTitle === "Raw Material Level (T)"
                        ? `${value}T`
                        : graphTitle === "Temperature (°C)"
                        ? `${value}°C`
                        : graphTitle === "Humidity (%)"
                        ? `${value}%`
                        : `${value}`
                      : graphTitle === "Operating Hours" ||
                        graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Safety Infractions" ||
                        graphTitle === "Alerts"
                      ? `${value}`
                      : `${value}Hrs`
                    : graphSequence === "graph4"
                    ? pageName === "analyticsPage"
                      ? graphTitle === "Energy Consumed (kWh)"
                        ? `${value}kWh`
                        : graphTitle === "Hours of Operation (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Predictive Maintenance Hours (Hrs)"
                        ? `${value}Hrs`
                        : graphTitle === "Equipment Efficiency (%)"
                        ? `${value}%`
                        : graphTitle === "Raw Material Level (T)"
                        ? `${value}T`
                        : graphTitle === "Temperature (°C)"
                        ? `${value}°C`
                        : graphTitle === "Humidity (%)"
                        ? `${value}%`
                        : `${value}`
                      : graphTitle === "Operating Hours" ||
                        graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : `${value}`
                    : graphSequence === "graph5"
                    ? graphTitle === "Energy Consumed (kWh)"
                      ? `${value}kWh`
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Predictive Maintenance Hours (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Equipment Efficiency (%)"
                      ? `${value}%`
                      : graphTitle === "Raw Material Level (T)"
                      ? `${value}T`
                      : graphTitle === "Temperature (°C)"
                      ? `${value}°C`
                      : graphTitle === "Humidity (%)"
                      ? `${value}%`
                      : `${value}`
                    : graphSequence === "graph6"
                    ? graphTitle === "Energy Consumed (kWh)"
                      ? `${value}kWh`
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Predictive Maintenance Hours (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Equipment Efficiency (%)"
                      ? `${value}%`
                      : graphTitle === "Raw Material Level (T)"
                      ? `${value}T`
                      : graphTitle === "Temperature (°C)"
                      ? `${value}°C`
                      : graphTitle === "Humidity (%)"
                      ? `${value}%`
                      : `${value}`
                    : graphSequence === "graph7"
                    ? graphTitle === "Energy Consumed (kWh)"
                      ? `${value}kWh`
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Predictive Maintenance Hours (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Equipment Efficiency (%)"
                      ? `${value}%`
                      : graphTitle === "Raw Material Level (T)"
                      ? `${value}T`
                      : graphTitle === "Temperature (°C)"
                      ? `${value}°C`
                      : graphTitle === "Humidity (%)"
                      ? `${value}%`
                      : `${value}`
                    : graphSequence === "graph8"
                    ? graphTitle === "Energy Consumed (kWh)"
                      ? `${value}kWh`
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Predictive Maintenance Hours (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Equipment Efficiency (%)"
                      ? `${value}%`
                      : graphTitle === "Raw Material Level (T)"
                      ? `${value}T`
                      : graphTitle === "Temperature (°C)"
                      ? `${value}°C`
                      : graphTitle === "Humidity (%)"
                      ? `${value}%`
                      : `${value}`
                    : graphSequence === "graph9"
                    ? graphTitle === "Energy Consumed (kWh)"
                      ? `${value}kWh`
                      : graphTitle === "Hours of Operation (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Predictive Maintenance Hours (Hrs)"
                      ? `${value}Hrs`
                      : graphTitle === "Equipment Efficiency (%)"
                      ? `${value}%`
                      : graphTitle === "Raw Material Level (T)"
                      ? `${value}T`
                      : graphTitle === "Temperature (°C)"
                      ? `${value}°C`
                      : graphTitle === "Humidity (%)"
                      ? `${value}%`
                      : `${value}`
                    : null
                }
            </td>
          </tr>
        </table>`;

              // return `<table>
              //               <tr>
              //               <td style="font-weight:bold;color:white ">
              //                 ${this.points.reduce(function (s, point) {
              //                   return (
              //                     point.series.name + ": " + point.y + "<br/>" + s
              //                   );
              //                 }, `<b> ${new Date(this.x).toLocaleDateString(
              //                   "nl-Nl"
              //                 )} </b>`)}
              //               </td>
              //         </tr>
              //       </table>`;
            } else {
              return false;
            }
          },
        },

        plotOptions: {
          // column: {
          //   dataLabels: {
          //     enabled: dataLabels,
          //     format:
          //       '<div style="font-size: 11px; line-height: 20px">${y}K</div>',
          //     backgroundColor: appTheme?.palette?.chartElement?.blueShade,
          //     color: appTheme?.palette?.chartElement?.colorWhite,
          //     y: -4,
          //     shadow: true,
          //     align: "center",
          //     shape: "circle",
          //   },
          // },
          series: {
            fillColor:
              graphTitle === "No. of Pallets"
                ? {
                    linearGradient: [0, 0, 0, 300],
                    stops: [
                      [0, appTheme?.palette?.chartElement?.blueDarkShade],
                      [
                        1,
                        Highcharts.color(
                          appTheme?.palette?.chartElement?.colorWhite
                        )
                          .setOpacity(0)
                          .get("rgba"),
                      ],
                    ],
                  }
                : graphTitle === "Energy Consumed (kWh)"
                ? {
                    linearGradient: [0, 0, 0, 300],

                    stops: [
                      [0, "#fff"],
                      [
                        1,
                        Highcharts.color("#765DFF").setOpacity(0).get("rgba"),
                      ],
                    ],
                  }
                : "",
            borderColor: "transparent",
            marker: {
              enabled: false,
            },

            lineWidth: splineWidth,
          },
          line: {
            dashStyle: "Dash",
          },
        },
        credits: {
          enabled: false,
        },
        series: dataPoints,
      }}
    />
  );
};

export default INF_Chart;
